import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface TechnicalSpec {
  inverterType?: string;
  inverterCount?: number;
  panelType?: string;
  panelCount?: number;
  gridConnectionVoltage?: string;
}

interface MarketConfig {
  marketModel?: string;
  balancingGroup?: string;
}

interface MeteringIntegration {
  id?: string;
  type?: string;
}

interface ForecastingConfig {
  id?: string;
  name?: string;
}

interface PlatformAsset {
  id: string;
  tenantId?: string;
  tenantName?: string;
  counterpartyId?: string;
  counterpartyName?: string;
  assetName: string;
  assetType?: string;
  eicCode?: string;
  installedCapacityMw?: number;
  region?: string;
  operationalStatus?: string;
  createdAt?: string;
  locationAddress?: string;
  latitude?: number;
  longitude?: number;
  commissioningDate?: string;
  technicalSpec?: TechnicalSpec;
  marketConfig?: MarketConfig;
  meteringIntegrations?: MeteringIntegration[];
  forecastingConfigs?: ForecastingConfig[];
  counts?: {
    contracts?: number;
    documents?: number;
  };
}

import { useTheme } from '@/hooks/useTheme';

export default function PlatformAssetsPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<PlatformAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const data = await api.get<PlatformAsset[]>('/admin/assets');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        addToast(getApiErrorMessage(err, 'Failed to load assets'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const expandedBg = isDark ? 'rgba(255,255,255,0.02)' : '#FAFAFA';

  const filtered = items.filter(
    (a) =>
      (a.assetName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (a.eicCode ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (a.tenantName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (a.counterpartyName ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      maintenance: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      commissioning: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      decommissioned: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
    };
    const style = map[status] ?? map.inactive;
    return (
      <span
        className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
        style={{ background: style.bg, color: style.color }}
      >
        {status}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={['Platform Admin', 'Assets Overview']}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
            Assets Overview
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B6B70' }}>
            System-wide view of all energy assets across all tenants
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl" style={{ border: '1px solid #E5E5E8' }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid #E5E5E8' }}>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md"
            style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
          >
            <i className="ri-search-line text-sm" style={{ color: '#6B6B70' }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, EIC code, tenant or counterparty..."
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: '#1A1A1A' }}
            />
          </div>
          <button
            onClick={loadAssets}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: '1px solid #E5E5E8' }}
            title="Refresh"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} style={{ color: '#6B6B70' }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
            <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>Loading assets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#F5F5F7' }}>
              <i className="ri-flashlight-line text-xl" style={{ color: '#6B6B70' }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>No assets found</p>
            <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>
              {search ? 'Try a different search term' : 'No assets in the system'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#F5F5F7' }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Tenant</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Counterparty</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Asset</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>EIC</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Capacity (MW)</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Region</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Contracts</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Docs</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Created</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((asset) => {
                  const isExpanded = expandedId === asset.id;
                  const ts = asset.technicalSpec;
                  const mc = asset.marketConfig;
                  const meteringCount = asset.meteringIntegrations?.length ?? 0;
                  const forecastCount = asset.forecastingConfigs?.length ?? 0;
                  return (
                    <>
                      <tr
                        key={asset.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => setExpandedId(isExpanded ? null : asset.id)}
                        style={{ borderBottom: '1px solid #F5F5F7' }}
                      >
                        <td className="px-4 py-3 text-xs font-medium" style={{ color: '#1A1A1A' }}>{asset.tenantName || '—'}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{asset.counterpartyName || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium" style={{ color: '#1A1A1A' }}>{asset.assetName}</div>
                        </td>
                        <td className="px-4 py-3 text-xs capitalize" style={{ color: '#6B6B70' }}>{asset.assetType || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: '#6B6B70' }}>{asset.eicCode || '—'}</td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {asset.installedCapacityMw !== undefined && asset.installedCapacityMw !== null ? Number(asset.installedCapacityMw).toFixed(2) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{asset.region || '—'}</td>
                        <td className="px-4 py-3">{getStatusBadge(asset.operationalStatus)}</td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {asset.counts?.contracts ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {asset.counts?.documents ?? 0}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                          {asset.createdAt ? new Date(asset.createdAt).toLocaleDateString('uk-UA') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                            <i className={`ri-arrow-down-s-line transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: '#6B6B70' }}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={12} className="px-0 py-0">
                            <div className="px-4 py-4" style={{ background: '#FAFAFA' }}>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Tenant ID</span>
                                  <div className="mt-1 font-mono text-xs" style={{ color: '#1A1A1A' }}>{asset.tenantId || '—'}</div>
                                </div>
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Counterparty ID</span>
                                  <div className="mt-1 font-mono text-xs" style={{ color: '#1A1A1A' }}>{asset.counterpartyId || '—'}</div>
                                </div>
                                {asset.locationAddress && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Location</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{asset.locationAddress}</div>
                                  </div>
                                )}
                                {(asset.latitude !== undefined || asset.longitude !== undefined) && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Coordinates</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>
                                      {asset.latitude !== undefined && asset.latitude !== null ? Number(asset.latitude).toFixed(5) : '—'}, {asset.longitude !== undefined && asset.longitude !== null ? Number(asset.longitude).toFixed(5) : '—'}
                                    </div>
                                  </div>
                                )}
                                {asset.commissioningDate && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Commissioning</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>
                                      {new Date(asset.commissioningDate).toLocaleDateString('uk-UA')}
                                    </div>
                                  </div>
                                )}
                                {ts?.inverterType && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Inverter</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>
                                      {ts.inverterType} ({ts.inverterCount ?? 0})
                                    </div>
                                  </div>
                                )}
                                {ts?.panelType && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Panels</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>
                                      {ts.panelType} ({ts.panelCount ?? 0})
                                    </div>
                                  </div>
                                )}
                                {ts?.gridConnectionVoltage && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Grid Voltage</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{ts.gridConnectionVoltage}</div>
                                  </div>
                                )}
                                {mc?.marketModel && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Market Model</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{mc.marketModel}</div>
                                  </div>
                                )}
                                {mc?.balancingGroup && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Balancing Group</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{mc.balancingGroup}</div>
                                  </div>
                                )}
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Metering Integrations</span>
                                  <div className="mt-1 text-sm font-medium" style={{ color: '#1A1A1A' }}>{meteringCount}</div>
                                </div>
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Forecasting Configs</span>
                                  <div className="mt-1 text-sm font-medium" style={{ color: '#1A1A1A' }}>{forecastCount}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}